import { Routes, Route, Navigate } from 'react-router-dom'
import { PublicLayout } from '@/layouts/PublicLayout'
import { AppShell } from '@/layouts/AppShell'
import { DashboardLayout } from '@/layouts/DashboardLayout'
import { SettingsLayout } from '@/layouts/SettingsLayout'
import { ExtranetLayout } from '@/layouts/ExtranetLayout'
import { LandingPage } from '@/features/public/LandingPage'
import { ContactPage } from '@/features/public/ContactPage'
import { IntranetLoginPage } from '@/features/auth/pages/IntranetLoginPage'
import { ExtranetLoginPage } from '@/features/auth/pages/ExtranetLoginPage'
import { OnboardingPage } from '@/features/auth/pages/OnboardingPage'
import { AccountCreationPage } from '@/features/auth/pages/AccountCreationPage'
import { DebugAuthPage } from '@/features/auth/pages/DebugAuthPage'
import { WelcomePage } from '@/features/auth/pages/WelcomePage'
import { ProtectedIntranetRoute } from '@/shared/components/ProtectedIntranetRoute'
import { ProtectedExtranetRoute } from '@/shared/components/ProtectedExtranetRoute'
import { PropertiesPage } from '@/features/properties/pages/PropertiesPage'
import { UnitsPage } from '@/features/units/pages/UnitsPage'
import { OwnersPage } from '@/features/owners/pages/OwnersPage'
import { DocumentsPage } from '@/features/documents/pages/DocumentsPage'
import { ExtranetDashboard } from '@/features/extranet/pages/ExtranetDashboard'
import { AgencySettingsPage } from '@/features/agencies/pages/AgencySettingsPage'
import { UserManagementPage } from '@/features/users/pages/UserManagementPage'
import { SubscriptionPage } from '@/features/settings/pages/SubscriptionPage'
import { NotificationsPage } from '@/features/settings/pages/NotificationsPage'
import { ProfilePage } from '@/features/settings/pages/ProfilePage'
import { DocumentTemplatesPage } from '@/features/settings/pages/DocumentTemplatesPage'
import { ExtranetPage } from '@/features/settings/pages/ExtranetPage'
import { IntegrationsPage } from '@/features/settings/pages/IntegrationsPage'
import { AuditLogsPage } from '@/features/admin/pages/AuditLogsPage'

export function AppRoutes() {
  return (
    <Routes>
      {/* ========================================
          PUBLIC ROUTES (Landing Page)
          No agency branding, default Kopro theme
      ======================================== */}
      <Route path="/" element={<PublicLayout />}>
        <Route index element={<LandingPage />} />
        <Route path="contact" element={<ContactPage />} />
        <Route path="account-creation" element={<AccountCreationPage />} />
      </Route>

      {/* ========================================
          AUTH ROUTES (Standalone with own theming)
          Authentication pages that manage their own layout and branding
      ======================================== */}
      <Route path="/auth/welcome" element={<WelcomePage />} />

      {/* ========================================
          APP ROUTES (With Agency Branding)
          All /app/* routes use agency-specific theme
      ======================================== */}
      <Route path="/app" element={<AppShell />}>
        {/* ========================================
            INTRANET ROUTES (Management Tool)
            For: USER, MANAGER, ADMIN
        ======================================== */}

        {/* Intranet Login */}
        <Route path="login" element={<IntranetLoginPage />} />

        {/* Debug Auth - for debugging session issues */}
        <Route path="debug-auth" element={<DebugAuthPage />} />

        {/* Onboarding - for first-time agency owners */}
        <Route
          path="onboarding"
          element={
            <ProtectedIntranetRoute requiresResidence={false}>
              <OnboardingPage />
            </ProtectedIntranetRoute>
          }
        />

        {/* Intranet Protected Routes */}
        <Route
          element={
            <ProtectedIntranetRoute>
              <DashboardLayout />
            </ProtectedIntranetRoute>
          }
        >
          <Route path="dashboard" element={<div>Dashboard (Coming soon)</div>} />
          <Route path="properties" element={<PropertiesPage />} />
          <Route path="units" element={<UnitsPage />} />
          <Route path="owners" element={<OwnersPage />} />
          <Route path="meetings" element={<div>Meetings (Coming soon)</div>} />
          <Route path="accounting" element={<div>Accounting (Coming soon)</div>} />

          {/* Documents Routes - Nested for clean URLs */}
          <Route path="documents">
            <Route index element={<DocumentsPage />} />
            <Route path="residences/:residenceId" element={<DocumentsPage />} />
            <Route path="residences/:residenceId/folders/:folderId" element={<DocumentsPage />} />
            <Route path="residences/:residenceId/folders/:folderId/documents/:docId" element={<DocumentsPage />} />
            <Route path="residences/:residenceId/documents/:docId" element={<DocumentsPage />} />
          </Route>
        </Route>

        {/* Admin Routes */}
        <Route
          path="admin"
          element={
            <ProtectedIntranetRoute>
              <DashboardLayout />
            </ProtectedIntranetRoute>
          }
        >
          <Route path="audit-logs" element={<AuditLogsPage />} />
        </Route>

        {/* Settings Routes (with dedicated Settings Layout) */}
        <Route
          path="settings"
          element={
            <ProtectedIntranetRoute>
              <DashboardLayout />
            </ProtectedIntranetRoute>
          }
        >
          <Route element={<SettingsLayout />}>
            <Route index element={<Navigate to="/app/settings/profile" replace />} />
            <Route path="users" element={<UserManagementPage />} />
            <Route path="subscription" element={<SubscriptionPage />} />
            <Route path="notifications" element={<NotificationsPage />} />
            <Route path="profile" element={<ProfilePage />} />
            <Route path="documents" element={<DocumentTemplatesPage />} />
            <Route path="agency" element={<AgencySettingsPage />} />
            <Route path="extranet" element={<ExtranetPage />} />
            <Route path="integrations" element={<IntegrationsPage />} />
          </Route>
        </Route>

        {/* ========================================
            EXTRANET ROUTES (Owner Portal)
            For: OWNER, COUNCIL_MEMBER
        ======================================== */}

        {/* Extranet Login */}
        <Route path="extranet/login" element={<ExtranetLoginPage />} />

        {/* Extranet Protected Routes */}
        <Route
          path="extranet"
          element={
            <ProtectedExtranetRoute>
              <ExtranetLayout />
            </ProtectedExtranetRoute>
          }
        >
          <Route path="dashboard" element={<ExtranetDashboard />} />
          <Route path="documents" element={<div>Mes documents (Coming soon)</div>} />
          <Route path="meetings" element={<div>Assemblées générales (Coming soon)</div>} />
          <Route path="messages" element={<div>Messages (Coming soon)</div>} />
        </Route>
      </Route>

      {/* ========================================
          DEFAULT REDIRECTS
      ======================================== */}

      {/* Catch all - redirect to landing */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
